import React, { useState, useEffect, useRef } from "react"
import { FiFilter } from "react-icons/fi"
import { IoCloseOutline } from "react-icons/io5"
import { AiOutlinePlus } from "react-icons/ai"

// Filter operator options based on field type
const OPERATORS = {
	string: [
		{ value: "equals", label: "Equals" },
		{ value: "contains", label: "Contains" },
		{ value: "starts_with", label: "Starts with" },
		{ value: "ends_with", label: "Ends with" },
		{ value: "is_empty", label: "Is empty" },
		{ value: "is_not_empty", label: "Is not empty" }
	],
	number: [
		{ value: "equals", label: "Equals" },
		{ value: "greater_than", label: "Greater than" },
		{ value: "less_than", label: "Less than" },
		{ value: "is_empty", label: "Is empty" },
		{ value: "is_not_empty", label: "Is not empty" }
	],
	time: [
		{ value: "equals", label: "Equals" },
		{ value: "greater_than", label: "After" },
		{ value: "less_than", label: "Before" },
		{ value: "between", label: "Between" },
		{ value: "is_empty", label: "Is empty" },
		{ value: "is_not_empty", label: "Is not empty" }
	],
	list: [
		{ value: "in_list", label: "Is any of" },
		{ value: "not_in_list", label: "Is none of" },
		{ value: "is_empty", label: "Is empty" },
		{ value: "is_not_empty", label: "Is not empty" }
	],
	boolean: [
		{ value: "equals", label: "Equals" },
		{ value: "is_empty", label: "Is empty" },
		{ value: "is_not_empty", label: "Is not empty" }
	],
	calculated: [
		{ value: "greater_than", label: "Greater than" },
		{ value: "less_than", label: "Less than" },
		{ value: "equals", label: "Equals" }
	]
}

// Patient status options
const PATIENT_STATUS_OPTIONS = [
	{ value: "Scheduled", label: "Scheduled" },
	{ value: "Add on", label: "Add on" },
	{ value: "Ongoing", label: "Ongoing" },
	{ value: "Completed", label: "Completed" },
	{ value: "No Show", label: "No Show" },
	{ value: "Cancellation", label: "Cancellation" }
]

// Convert minutes since midnight to HH:MM format
const formatTimeFromMinutes = minutes => {
	if (!minutes && minutes !== 0) return ""

	const hours = Math.floor(minutes / 60)
	const mins = minutes % 60
	const period = hours >= 12 ? "PM" : "AM"
	const formattedHours = hours % 12 === 0 ? 12 : hours % 12
	const formattedMins = mins.toString().padStart(2, "0")

	return `${formattedHours}:${formattedMins} ${period}`
}

const PatientFilter = ({
	columns,
	applyFilters,
	nurseSchedule,
	chairSchedule,
	initialFilters = [],
	initialFilterRelationship = "AND",
	patientSchedule = []
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [filters, setFilters] = useState(initialFilters)
	const [activeFilter, setActiveFilter] = useState(null)
	const filterMenuRef = useRef(null)
	const [filterRelationship, setFilterRelationship] = useState(initialFilterRelationship)

	// Get all available nurses and chairs for dropdown selection
	const nurseOptions = nurseSchedule.map(nurse => ({
		value: nurse.nurseId,
		label: nurse.nurseName
	}))

	const chairOptions = chairSchedule.map(chair => ({
		value: chair.chairId,
		label: chair.chairName
	}))

	// Extract unique infusion types from patient data
	const infusionTypeOptions = React.useMemo(() => {
		const uniqueTypes = [...new Set(patientSchedule.map(p => p.infusionType).filter(Boolean))]
		return uniqueTypes.map(type => ({
			value: type,
			label: type
		}))
	}, [patientSchedule])

	// Extract unique departments from patient data
	const departmentOptions = React.useMemo(() => {
		const uniqueDepts = [...new Set(patientSchedule.map(p => p.department).filter(Boolean))]
		return uniqueDepts.map(dept => ({
			value: dept,
			label: dept
		}))
	}, [patientSchedule])

	// Extract unique acuity levels from patient data
	const acuityOptions = React.useMemo(() => {
		const uniqueAcuities = [...new Set(patientSchedule.map(p => p.acuity).filter(Boolean))]
		return uniqueAcuities
			.sort((a, b) => Number(a) - Number(b))
			.map(acuity => ({
				value: acuity.toString(),
				label: `${acuity}`
			}))
	}, [patientSchedule])

	// Extract unique clinic appointment types from patient data
	const clinicAppointmentTypeOptions = React.useMemo(() => {
		const uniqueTypes = [
			...new Set(patientSchedule.map(p => p.linkedClinicAppointment).filter(Boolean))
		]
		return uniqueTypes.map(type => ({
			value: type,
			label: type
		}))
	}, [patientSchedule])

	// Extract unique cost information from patient data
	const costInformationOptions = React.useMemo(() => {
		const uniqueCosts = [
			...new Set(patientSchedule.map(p => p.costInformation).filter(Boolean))
		]
		return uniqueCosts.map(cost => ({
			value: cost,
			label: cost
		}))
	}, [patientSchedule])

	// List of available time fields for calculation filter
	const timeFields = columns
		.filter(col => col.dataType === "time")
		.map(col => ({
			value: col.id,
			label: col.name
		}))

	useEffect(() => {
		// Close dropdown when clicking outside
		const handleClickOutside = event => {
			if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [])

	// Set filters and relationship from props on mount and when they change
	useEffect(() => {
		setFilters(initialFilters)
		setFilterRelationship(initialFilterRelationship)
	}, [initialFilters, initialFilterRelationship])

	// Add a new filter
	const addFilter = () => {
		if (columns.length === 0) return

		const newFilter = {
			id: Math.random().toString(36).substring(2, 9),
			property: columns[0].id,
			operator: getOperatorsByType(columns[0].dataType)[0].value,
			value: "",
			values: [] // For multi-select options
		}

		const updatedFilters = [...filters, newFilter]
		setFilters(updatedFilters)
		setActiveFilter(newFilter)

		// Auto-apply the filter
		applyFilters(updatedFilters, filterRelationship)
	}

	// Add a calculation filter (time difference)
	const addCalculationFilter = () => {
		if (timeFields.length < 2) return

		const newFilter = {
			id: Math.random().toString(36).substring(2, 9),
			type: "calculation",
			field1: timeFields[0].value,
			field2: timeFields[1].value,
			operator: "greater_than",
			value: 60 // Default 60 minutes
		}

		const updatedFilters = [...filters, newFilter]
		setFilters(updatedFilters)
		setActiveFilter(newFilter)

		// Auto-apply the filter
		applyFilters(updatedFilters, filterRelationship)
	}

	// Remove a filter
	const removeFilter = id => {
		const updatedFilters = filters.filter(f => f.id !== id)
		setFilters(updatedFilters)
		if (activeFilter?.id === id) {
			setActiveFilter(null)
		}

		// Auto-apply the filter
		applyFilters(updatedFilters, filterRelationship)
	}

	// Update a filter
	const updateFilter = (id, updates) => {
		const updatedFilters = filters.map(f => (f.id === id ? { ...f, ...updates } : f))
		setFilters(updatedFilters)

		// Auto-apply the filter
		applyFilters(updatedFilters, filterRelationship)
	}

	// Clear all filters
	const clearFilters = () => {
		setFilters([])
		applyFilters([], filterRelationship)
		setIsOpen(false)
	}

	// Toggle filter relationship between AND/OR
	const toggleFilterRelationship = () => {
		const newRelationship = filterRelationship === "AND" ? "OR" : "AND"
		setFilterRelationship(newRelationship)
		applyFilters(filters, newRelationship)
	}

	// Get operators based on data type
	const getOperatorsByType = dataType => {
		switch (dataType) {
			case "number":
				return OPERATORS.number
			case "time":
				return OPERATORS.time
			case "list":
				return OPERATORS.list
			case "boolean":
				return OPERATORS.boolean
			default:
				return OPERATORS.string
		}
	}

	// Get filtered columns based on search
	const filteredColumns = columns

	// Get input component based on data type and operator
	const getInputComponent = filter => {
		// Skip for empty/not empty operators
		if (["is_empty", "is_not_empty"].includes(filter.operator)) {
			return null
		}

		// Get column definition
		const column = columns.find(c => c.id === filter.property)
		if (!column) return null

		switch (column.dataType) {
			case "number":
				if (filter.property === "acuity") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{acuityOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`acuity-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`acuity-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				}

				return (
					<input
						type="number"
						value={filter.value}
						onChange={e => updateFilter(filter.id, { value: e.target.value })}
						className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
					/>
				)

			case "time":
				if (filter.operator === "between") {
					return (
						<div className="flex flex-col space-y-2">
							<div className="flex items-center space-x-2">
								<div className="flex-1">
									<input
										type="time"
										value={
											filter.startTime
												? formatTimeForInput(filter.startTime)
												: ""
										}
										onChange={e => {
											const minutesValue = parseTimeInputToMinutes(
												e.target.value
											)
											updateFilter(filter.id, { startTime: minutesValue })
										}}
										className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
									/>
									<div className="text-xs text-gray-500 mt-1">
										{filter.startTime
											? formatTimeFromMinutes(filter.startTime)
											: ""}
									</div>
								</div>
								<span className="text-sm font-medium">to</span>
								<div className="flex-1">
									<input
										type="time"
										value={
											filter.endTime ? formatTimeForInput(filter.endTime) : ""
										}
										onChange={e => {
											const minutesValue = parseTimeInputToMinutes(
												e.target.value
											)
											updateFilter(filter.id, { endTime: minutesValue })
										}}
										className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
									/>
									<div className="text-xs text-gray-500 mt-1">
										{filter.endTime
											? formatTimeFromMinutes(filter.endTime)
											: ""}
									</div>
								</div>
							</div>
							<div className="text-xs text-gray-500">24-hour format</div>
						</div>
					)
				}
				return (
					<div className="flex flex-col space-y-2">
						<input
							type="time"
							value={filter.value ? formatTimeForInput(filter.value) : ""}
							onChange={e => {
								const minutesValue = parseTimeInputToMinutes(e.target.value)
								updateFilter(filter.id, { value: minutesValue })
							}}
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
						/>
						<div className="text-xs text-gray-500 flex justify-between">
							<span>24-hour format</span>
							<span>{filter.value ? formatTimeFromMinutes(filter.value) : ""}</span>
						</div>
					</div>
				)

			case "list":
				if (filter.property === "nurse") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{nurseOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`nurse-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`nurse-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "chair") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{chairOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`chair-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`chair-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "visitStatus") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{PATIENT_STATUS_OPTIONS.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`status-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`status-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "infusionType") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{infusionTypeOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`infusion-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`infusion-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "referringDepartment") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{departmentOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`dept-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`dept-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "acuity") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{acuityOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`acuity-list-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`acuity-list-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "linkedClinicAppointment") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{clinicAppointmentTypeOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`clinic-type-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`clinic-type-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "costInformation") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{costInformationOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`cost-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`cost-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				}
				return (
					<input
						type="text"
						value={filter.value}
						onChange={e => updateFilter(filter.id, { value: e.target.value })}
						className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
					/>
				)

			case "boolean":
				return (
					<select
						value={filter.value}
						onChange={e => updateFilter(filter.id, { value: e.target.value })}
						className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
					>
						<option value="true">True</option>
						<option value="false">False</option>
					</select>
				)

			default:
				if (filter.property === "infusionType") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{infusionTypeOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`infusion-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`infusion-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				} else if (filter.property === "referringDepartment") {
					return (
						<div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md">
							{departmentOptions.map(option => (
								<div
									key={option.value}
									className="flex items-center px-3 py-2 hover:bg-gray-100"
								>
									<input
										type="checkbox"
										id={`dept-${option.value}`}
										checked={filter.values?.includes(option.value)}
										onChange={e => {
											const updatedValues = e.target.checked
												? [...(filter.values || []), option.value]
												: (filter.values || []).filter(
														v => v !== option.value
													)
											updateFilter(filter.id, { values: updatedValues })
										}}
										className="h-4 w-4 text-customBlue border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
									/>
									<label
										htmlFor={`dept-${option.value}`}
										className="ml-2 text-sm text-gray-700"
									>
										{option.label}
									</label>
								</div>
							))}
						</div>
					)
				}

				return (
					<input
						type="text"
						value={filter.value}
						onChange={e => updateFilter(filter.id, { value: e.target.value })}
						className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
						placeholder="Value"
					/>
				)
		}
	}

	// Format time for HTML5 time input (HH:MM)
	const formatTimeForInput = minutes => {
		if (!minutes && minutes !== 0) return ""

		const hours = Math.floor(minutes / 60)
			.toString()
			.padStart(2, "0")
		const mins = (minutes % 60).toString().padStart(2, "0")

		return `${hours}:${mins}`
	}

	// Parse time from HTML5 time input (HH:MM) to minutes
	const parseTimeInputToMinutes = timeString => {
		if (!timeString) return ""

		const [hours, minutes] = timeString.split(":").map(Number)
		return hours * 60 + minutes
	}

	// Get calculation filter component
	const getCalculationComponent = filter => {
		return (
			<div className="space-y-2">
				<div className="flex items-center space-x-2 flex-wrap">
					<select
						value={filter.field1}
						onChange={e => updateFilter(filter.id, { field1: e.target.value })}
						className="w-[calc(50%-0.25rem)] px-3 py-2 text-sm border border-gray-300 rounded-md"
					>
						{timeFields.map(field => (
							<option key={field.value} value={field.value}>
								{field.label}
							</option>
						))}
					</select>
					<span className="text-sm">-</span>
					<select
						value={filter.field2}
						onChange={e => updateFilter(filter.id, { field2: e.target.value })}
						className="w-[calc(50%-0.25rem)] px-3 py-2 text-sm border border-gray-300 rounded-md"
					>
						{timeFields.map(field => (
							<option key={field.value} value={field.value}>
								{field.label}
							</option>
						))}
					</select>
				</div>

				<div className="flex items-center space-x-2">
					<select
						value={filter.operator}
						onChange={e => updateFilter(filter.id, { operator: e.target.value })}
						className="w-2/5 px-3 py-2 text-sm border border-gray-300 rounded-md"
					>
						{OPERATORS.calculated.map(op => (
							<option key={op.value} value={op.value}>
								{op.label}
							</option>
						))}
					</select>

					<div className="flex items-center flex-1">
						<input
							type="number"
							value={filter.value}
							onChange={e => updateFilter(filter.id, { value: e.target.value })}
							className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-md"
						/>
						<span className="ml-2 text-sm text-gray-500">minutes</span>
					</div>
				</div>
			</div>
		)
	}

	// Get a summary of the filter for the collapsed view
	const getFilterSummary = filter => {
		if (filter.type === "calculation") {
			const field1Name =
				timeFields.find(f => f.value === filter.field1)?.label || filter.field1
			const field2Name =
				timeFields.find(f => f.value === filter.field2)?.label || filter.field2
			const operatorLabel =
				OPERATORS.calculated.find(op => op.value === filter.operator)?.label ||
				filter.operator

			return `${field1Name} - ${field2Name} ${operatorLabel.toLowerCase()} ${filter.value} minutes`
		}

		const column = columns.find(c => c.id === filter.property)
		if (!column) return ""

		const operatorLabel =
			getOperatorsByType(column.dataType).find(op => op.value === filter.operator)?.label ||
			filter.operator

		if (["is_empty", "is_not_empty"].includes(filter.operator)) {
			return `${column.name} ${operatorLabel.toLowerCase()}`
		}

		if (filter.operator === "in_list" || filter.operator === "not_in_list") {
			let valueLabel = ""
			if (filter.property === "nurse") {
				valueLabel = (filter.values || [])
					.map(v => nurseOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "chair") {
				valueLabel = (filter.values || [])
					.map(v => chairOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "visitStatus") {
				valueLabel = (filter.values || []).join(", ")
			} else if (filter.property === "infusionType") {
				valueLabel = (filter.values || [])
					.map(v => infusionTypeOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "referringDepartment") {
				valueLabel = (filter.values || [])
					.map(v => departmentOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "acuity") {
				valueLabel = (filter.values || [])
					.map(v => acuityOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "linkedClinicAppointment") {
				valueLabel = (filter.values || [])
					.map(v => clinicAppointmentTypeOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			} else if (filter.property === "costInformation") {
				valueLabel = (filter.values || [])
					.map(v => costInformationOptions.find(opt => opt.value === v)?.label || v)
					.join(", ")
			}

			if (valueLabel.length > 20) {
				valueLabel = valueLabel.substring(0, 20) + "..."
			}

			return `${column.name} ${operatorLabel.toLowerCase()} ${valueLabel}`
		}

		let valueLabel = filter.value

		// Format time values
		if (column.dataType === "time") {
			if (filter.operator === "between") {
				const startFormatted = filter.startTime
					? formatTimeFromMinutes(filter.startTime)
					: ""
				const endFormatted = filter.endTime ? formatTimeFromMinutes(filter.endTime) : ""
				valueLabel = `${startFormatted} - ${endFormatted}`
			} else if (filter.value) {
				valueLabel = formatTimeFromMinutes(filter.value)
			}
		}

		if (valueLabel && valueLabel.length > 20) {
			valueLabel = valueLabel.substring(0, 20) + "..."
		}

		return `${column.name} ${operatorLabel.toLowerCase()} ${valueLabel || ""}`
	}

	// Return the component
	return (
		<div className="relative" ref={filterMenuRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none flex items-center gap-2"
			>
				<FiFilter className="h-4 w-4 text-gray-600" />
				<span className="font-medium">Filter</span>
				{filters.length > 0 && (
					<span className="ml-2 bg-customBlue bg-opacity-20 text-customBlue text-xs font-semibold rounded-full px-2 py-0.5">
						{filters.length}
					</span>
				)}
			</button>

			{isOpen && (
				<div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[50] w-96">
					<div className="p-4">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-medium text-gray-900">Filters</h3>
							<button
								onClick={() => setIsOpen(false)}
								className="text-gray-400 hover:text-gray-500"
							>
								<IoCloseOutline className="h-5 w-5" />
							</button>
						</div>

						{/* Filter relationship toggle */}
						{filters.length > 1 && (
							<div className="mb-4 flex items-center">
								<span className="text-sm text-gray-600 mr-2">
									Filter relationship:
								</span>
								<div className="flex border border-gray-300 rounded-md overflow-hidden">
									<button
										className={`px-3 py-1 text-sm ${
											filterRelationship === "AND"
												? "bg-customBlue text-white"
												: "bg-gray-50 text-gray-700"
										}`}
										onClick={() => {
											if (filterRelationship !== "AND")
												toggleFilterRelationship()
										}}
									>
										AND
									</button>
									<button
										className={`px-3 py-1 text-sm ${
											filterRelationship === "OR"
												? "bg-customBlue text-white"
												: "bg-gray-50 text-gray-700"
										}`}
										onClick={() => {
											if (filterRelationship !== "OR")
												toggleFilterRelationship()
										}}
									>
										OR
									</button>
								</div>
								<div className="ml-2 text-xs text-gray-500">
									{filterRelationship === "AND"
										? "Matches all conditions"
										: "Matches any condition"}
								</div>
							</div>
						)}

						{/* Filters list */}
						<div className="space-y-3 max-h-96 overflow-y-auto">
							{filters.map(filter => (
								<div
									key={filter.id}
									className={`p-3 rounded-lg border ${
										activeFilter?.id === filter.id
											? "border-customBlue bg-customBlue bg-opacity-5"
											: "border-gray-200"
									}`}
									onClick={() => setActiveFilter(filter)}
								>
									{filter.type === "calculation" ? (
										// Calculation filter
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<div className="font-medium text-sm text-gray-700">
													Time Difference
												</div>
												<button
													onClick={e => {
														e.stopPropagation()
														removeFilter(filter.id)
													}}
													className="text-gray-400 hover:text-gray-500"
												>
													<IoCloseOutline className="h-5 w-5" />
												</button>
											</div>
											{activeFilter?.id === filter.id ? (
												getCalculationComponent(filter)
											) : (
												<div className="text-sm text-gray-600 mt-1">
													{getFilterSummary(filter)}
												</div>
											)}
										</div>
									) : (
										// Standard filter
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<select
													value={filter.property}
													onChange={e => {
														const column = columns.find(
															c => c.id === e.target.value
														)
														const operators = getOperatorsByType(
															column?.dataType || "string"
														)
														updateFilter(filter.id, {
															property: e.target.value,
															operator: operators[0].value,
															value: "",
															values: []
														})
													}}
													className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:border-customBlue focus:ring-0"
												>
													{filteredColumns.map(column => (
														<option key={column.id} value={column.id}>
															{column.name}
														</option>
													))}
												</select>
												<button
													onClick={e => {
														e.stopPropagation()
														removeFilter(filter.id)
													}}
													className="ml-2 text-gray-400 hover:text-gray-500"
												>
													<IoCloseOutline className="h-5 w-5" />
												</button>
											</div>

											{activeFilter?.id === filter.id ? (
												<div>
													<select
														value={filter.operator}
														onChange={e =>
															updateFilter(filter.id, {
																operator: e.target.value
															})
														}
														className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md mb-2 focus:border-customBlue focus:ring-0"
													>
														{getOperatorsByType(
															columns.find(
																c => c.id === filter.property
															)?.dataType || "string"
														).map(op => (
															<option key={op.value} value={op.value}>
																{op.label}
															</option>
														))}
													</select>

													{getInputComponent(filter)}
												</div>
											) : (
												<div className="text-sm text-gray-600">
													{getFilterSummary(filter)}
												</div>
											)}
										</div>
									)}
								</div>
							))}

							{/* Add filter buttons */}
							<div className="flex space-x-2">
								<button
									onClick={addFilter}
									className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
								>
									<AiOutlinePlus className="mr-2 h-4 w-4" />
									Add filter
								</button>
								{timeFields.length >= 2 && (
									<button
										onClick={addCalculationFilter}
										className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
									>
										<AiOutlinePlus className="mr-2 h-4 w-4" />
										Add time diff
									</button>
								)}
							</div>
						</div>

						{/* Action buttons */}
						<div className="flex justify-between mt-4 pt-3 border-t border-gray-200">
							<button
								onClick={clearFilters}
								className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
							>
								Clear all
							</button>
							<button
								onClick={() => setIsOpen(false)}
								className="px-4 py-2 text-sm font-medium bg-customBlue text-white rounded-md hover:bg-customBlue-dark"
							>
								Done
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default PatientFilter
